import { Settings, StreamrWrapper, } from '@streamr/tv-engine';
import { areEqualBinaries, toUserId, } from '@streamr/utils';
import { Mutex } from 'async-mutex';
import { EventEmitter } from 'eventemitter3';
import { MediaPlayer } from '../engine/mediaplayer/MediaPlayer';
import { PluginApiService } from '../services/pluginapi/PluginApiService';
import { PurchaseService } from '../services/purchase/PurchaseService';
export class StreamrTvViewer extends EventEmitter {
    streamrWrapper;
    mediaPlayer;
    mutex;
    heartbeatTimeoutRef;
    heartbeatIntervalRef;
    audioMuted = true;
    videoProperties;
    streamId;
    broadcasterUserId;
    aesKey;
    canvas;
    constructor(opts) {
        super();
        this.streamId = opts.streamId;
        this.broadcasterUserId = opts.broadcasterUserId;
        this.aesKey = opts.aesKey;
        this.mediaPlayer = new MediaPlayer();
        this.mutex = new Mutex();
        this.mediaPlayer.on('audioMuted', this.onAudioMuted);
        this.mediaPlayer.on('audioUnmuted', this.onAudioUnmuted);
        this.mediaPlayer.on('videoPropertiesChanged', this.onVideoPropertiesChanged);
    }
    async setCanvas(canvas) {
        console.log('setCanvas called for viewer', canvas);
        this.canvas = canvas;
        await this.mediaPlayer.start(canvas);
        console.log('mediaPlayer started for viewer', canvas);
    }
    async start() {
        await this.mutex.acquire();
        try {
            this.streamrWrapper = new StreamrWrapper({
                aesKey: this.aesKey,
            });
            const pluginApiService = PluginApiService.getInstance();
            pluginApiService.setStreamrWrapper(this.streamrWrapper);
            const purchaseService = PurchaseService.getInstance();
            purchaseService.setStreamrWrapper(this.streamrWrapper);
            this.streamrWrapper.on('mediaPacketFragment', this.onMediaPacketFragment);
            this.streamrWrapper.on('broadcastMetadata', this.onBroadcastMetadataChanged);
            this.streamrWrapper.on('purchaseConfirmedMessage', this.onPurchaseConfirmedMessage);
            await this.streamrWrapper.start(this.streamId);
            this.setupHeartbeat();
            console.log('streamrWrapper started for viewer', this.streamId);
        }
        finally {
            this.mutex.release();
        }
    }
    async stop() {
        await this.mutex.acquire();
        try {
            this.clearHeartbeatTimers();
            window.removeEventListener('beforeunload', this.onBeforeUnload);
            this.sendLeave();
            this.streamrWrapper?.off('mediaPacketFragment', this.onMediaPacketFragment);
            this.streamrWrapper?.off('broadcastMetadata', this.onBroadcastMetadataChanged);
            await this.mediaPlayer.stop();
            await this.streamrWrapper?.stop();
        }
        finally {
            this.mutex.release();
        }
    }
    async muteAudio() {
        await this.mediaPlayer.mute();
    }
    async unmuteAudio() {
        await this.mediaPlayer.unmute();
    }
    isAudioMuted() {
        return this.audioMuted;
    }
    getVideoProperties() {
        return this.videoProperties;
    }
    clearHeartbeatTimers() {
        if (this.heartbeatTimeoutRef) {
            clearTimeout(this.heartbeatTimeoutRef);
            this.heartbeatTimeoutRef = undefined;
        }
        if (this.heartbeatIntervalRef) {
            clearInterval(this.heartbeatIntervalRef);
            this.heartbeatIntervalRef = undefined;
        }
    }
    onAudioMuted = () => {
        this.audioMuted = true;
        this.emit('muteChange', true);
    };
    onAudioUnmuted = () => {
        this.audioMuted = false;
        this.emit('muteChange', false);
    };
    onVideoPropertiesChanged = (value) => {
        this.videoProperties = value;
        this.emit('videoPropertiesChanged', value);
    };
    onMediaPacketFragment = (packet, publisherId, messageChainId) => {
        if (!areEqualBinaries(publisherId, this.broadcasterUserId)) {
            console.log('Skipping broadcast metadata change (message is not from broadcaster)');
            return;
        }
        this.mediaPlayer.feedMediaPacketFragment(packet, toUserId(publisherId), messageChainId);
        this.emit('receivedFrame');
    };
    onBroadcastMetadataChanged = (broadcastMetadata, publisherId) => {
        if (!areEqualBinaries(publisherId, this.broadcasterUserId)) {
            console.log('Skipping broadcast metadata change (message is not from broadcaster)');
            return;
        }
        this.emit('broadcastMetadataChanged', broadcastMetadata);
    };
    sendLeave = () => {
        if (!this.streamrWrapper) {
            return;
        }
        this.streamrWrapper.publishLeave();
    };
    sendHeartbeat = () => {
        if (!this.streamrWrapper) {
            return;
        }
        this.streamrWrapper.publishHeartbeat(false);
    };
    onBeforeUnload = () => {
        this.sendLeave();
    };
    setupHeartbeat() {
        window.addEventListener('beforeunload', this.onBeforeUnload);
        this.sendHeartbeat();
        const randomDelay = Math.floor(Math.random() * Settings.defaultHeartbeatInterval);
        this.heartbeatTimeoutRef = setTimeout(() => {
            this.heartbeatTimeoutRef = undefined;
            this.sendHeartbeat();
            this.clearHeartbeatTimers();
            this.heartbeatIntervalRef = setInterval(() => {
                this.sendHeartbeat();
            }, Settings.defaultHeartbeatInterval);
        }, randomDelay);
    }
    onPurchaseConfirmedMessage = (message, publisherId) => {
        if (!areEqualBinaries(publisherId, this.broadcasterUserId)) {
            console.log('Skipping broadcast metadata change (message is not from broadcaster)');
            return;
        }
        PurchaseService.getInstance().onPurchaseConfirmedMessage(message);
    };
}
